import React, { useState, useCallback } from 'react';
import { Firestore, addDoc, collection, serverTimestamp } from 'firebase/firestore';

interface ClubSignupProps {
  // Injected (not imported) so the component stays easy to test and doesn't
  // couple to firebase init — same pattern as WaitlistSignup.
  db: Firestore | null;
}

type StatusKind = 'success' | 'error' | '';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "Join the Club / Order TrueNooch direct" signup.
 * Name + email are required; everything else is optional survey data that
 * makes fulfilment and the founding-member selection smarter. Writes a fixed
 * 10-key shape to the `clubMembers` collection (matches firestore.rules).
 */
export function ClubSignup({ db }: ClubSignupProps) {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [area, setArea] = useState('');
  const [discovered, setDiscovered] = useState('');
  const [frequency, setFrequency] = useState('');
  const [status, setStatus] = useState<{ msg: string; kind: StatusKind }>({ msg: '', kind: '' });
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState<string | null>(null); // first name once they're in

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const name = firstName.trim();
    const mail = email.trim().toLowerCase();
    if (!name) {
      setStatus({ msg: 'Please add your first name.', kind: 'error' });
      return;
    }
    if (!mail || !EMAIL_RE.test(mail)) {
      setStatus({ msg: 'Please enter a valid email address.', kind: 'error' });
      return;
    }
    if (!db) {
      // Firebase didn't initialize — fall back to email so the visitor still has a path.
      const body = `Name: ${name}\nEmail: ${mail}\nPhone: ${phone}\nNeighborhood/ZIP: ${area}`;
      window.location.href =
        'mailto:jarvis@truenooch.com?subject=' +
        encodeURIComponent('Join the TrueNooch club') +
        '&body=' + encodeURIComponent(body);
      return;
    }
    setSubmitting(true);
    setStatus({ msg: 'Saving…', kind: '' });
    try {
      await addDoc(collection(db, 'clubMembers'), {
        firstName: name,
        email: mail,
        phone: phone.trim(),
        area: area.trim(),
        discovered,
        frequency,
        foundingMember: false,
        source: 'truenooch.com',
        page: 'club',
        createdAt: serverTimestamp(),
      });
      setFirstName(''); setEmail(''); setPhone(''); setArea(''); setDiscovered(''); setFrequency('');
      setJoined(name); // switches the form out for the welcome
    } catch (err) {
      console.error('Club signup failed:', err);
      setStatus({
        msg: 'Something went wrong. Try again, or email jarvis@truenooch.com.',
        kind: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }, [firstName, email, phone, area, discovered, frequency, db]);

  if (joined) {
    return (
      <section className="section fade-in" style={{ paddingTop: '2rem' }}>
        <div className="signup club-signup club-welcome" role="status" aria-live="polite">
          <div className="club-welcome-mark" aria-hidden="true">✓</div>
          <h3>You’re in{joined ? `, ${joined}` : ''}.</h3>
          <p>Welcome to the club — you’re one of our founding regulars now.</p>
          <p>
            I read every one of these myself, so I’ll be in touch before long. When the next small
            batch comes out of the kitchen, you’ll be among the first to know.
          </p>
          <p className="club-promise">Made by hand in Colorado. Glad you’re at the table.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="section fade-in" style={{ paddingTop: '2rem' }}>
      <div className="signup club-signup">
        <h3>Join the club</h3>
        <p>Two things and you’re in — the rest is optional.</p>
        <form className="club-form" onSubmit={handleSubmit} noValidate>
          <div className="club-row">
            <div className="club-field">
              <label htmlFor="club-name">First name</label>
              <input id="club-name" type="text" value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Your name" required autoComplete="given-name" />
            </div>
            <div className="club-field">
              <label htmlFor="club-email">Email</label>
              <input id="club-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com" required autoComplete="email" />
            </div>
          </div>

          <div className="club-divider"><span>Tell us a little more · optional</span></div>

          <div className="club-row">
            <div className="club-field">
              <label htmlFor="club-phone">Phone <span className="club-hint">(for the occasional “fresh batch” text)</span></label>
              <input id="club-phone" type="tel" value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(303) …" autoComplete="tel" />
            </div>
            <div className="club-field">
              <label htmlFor="club-area">Neighborhood or ZIP</label>
              <input id="club-area" type="text" value={area}
                onChange={(e) => setArea(e.target.value)}
                placeholder="e.g. RiNo / 80205" />
            </div>
          </div>

          <div className="club-field">
            <label htmlFor="club-discovered">Where did you first try TrueNooch?</label>
            <select id="club-discovered" value={discovered} onChange={(e) => setDiscovered(e.target.value)}>
              <option value="">Choose one…</option>
              <option>A yoga studio</option>
              <option>A run / ride club</option>
              <option>An event or market</option>
              <option>A friend shared it</option>
              <option>Haven’t yet — just curious</option>
            </select>
          </div>

          <div className="club-field">
            <label htmlFor="club-frequency">How often could you see yourself reaching for it?</label>
            <select id="club-frequency" value={frequency} onChange={(e) => setFrequency(e.target.value)}>
              <option value="">Choose one…</option>
              <option>A few times a week</option>
              <option>About weekly</option>
              <option>Every couple weeks</option>
              <option>Now and then</option>
            </select>
          </div>

          <button type="submit" className="club-submit" disabled={submitting}>Join the Club</button>
        </form>
        <div className={`signup-status ${status.kind}`} role="status" aria-live="polite">
          {status.msg}
        </div>
        <p className="club-promise">Founding-regular invitations go out to our earliest members first.</p>
      </div>
    </section>
  );
}
